/* @flow */

import React, { useContext } from "react";
import { useData } from "crustate/react";
import { useBrowser } from "@awardit/react-use-browser";
import { useUi, MODE } from "helpers/ui";
import { HomeData, CmsBlockData } from "state/data";
import { StoreInfoContext } from "entrypoint/shared";
import { Helmet } from "react-helmet-async";
import { useTranslate } from "@awardit/react-use-translate";
import { formatMeta } from "helpers/get-meta";
import { Button, Wrapper, ButtonLink, PromotionBlock, PromotionWrapper } from "@crossroads/ui-components";
import Hero from "components/Hero";
import CategorySection from "components/CategorySection";
import Loading from "pages/Loading";
import { Offerings } from "@crossroads/shop-views";
import useBrowserDimensions from "helpers/hooks/use-browser-dimensions";
import ProductCard from "components/ProductCard";
import Arrow from "icons/arrow.svg";

import styles from "./styles.scss";

const Home = (): React$Node => {
  const browser = useBrowser();
  const t = useTranslate();
  const home = useData(HomeData);
  const offerings = useData(CmsBlockData);
  const { info } = useContext(StoreInfoContext);
  const { isDesktop } = useUi();
  const { width: browserWidth } = useBrowserDimensions();
  const offeringsContent = offerings.state === "LOADED" && offerings.data ?
    offerings.data.content : null;
  const {
    setHamburgerOpen,
    setSubNavOpen,
    setViewMode,
    viewMode,
  } = useUi();

  if (home.state !== "LOADED") {
    return <Loading />;
  }

  const meta = formatMeta({
    title: info.defaultTitle,
    description: info.defaultDescription,
  });

  const getPopularProductAmount = () => {
    if (browserWidth < Number.parseInt(styles.tinySmall, 10)) {
      return 2;
    }

    if (browserWidth < Number.parseInt(styles.medium, 10)) {
      return 3;
    }

    return 4;
  };

  return (
    <div className={styles.home}>
      <Helmet
        title={meta.title}
        meta={meta.data}
      />

      <h1 className="sr-only">{meta.title}</h1>

      {home.data.hero &&
        <Hero
          smallImage={home.data.hero.block.smallImage}
          largeImage={home.data.hero.block.largeImage}
          modifier={home.data.hero.block.modifier}
          heading={home.data.hero.block.heading}
          subHeading={home.data.hero.block.subHeading}
          splash={home.data.hero.block.splash}
          text={home.data.hero.block.text}
          link={home.data.hero.block.link}
          CTA={home.data.hero.block.cta}
        />
      }

      <Wrapper>
        <div className={styles.popularCategories}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t("HOMEVIEW.POPULAR_CATEGORIES")}</h2>
            <Button
              type="button"
              aria-controls={isDesktop ? "desktopCategories" : "categoriesMobile"}
              aria-expanded={viewMode === MODE.CATEGORIES ?? "false"}
              variant="blank"
              className={styles.cta}
              onClick={() => {
                setHamburgerOpen(true);
                setSubNavOpen(true);
                setViewMode(MODE.CATEGORIES);
              }}
            >
              {t("HOMEVIEW.ALL_CATEGORIES")}
              <Arrow aria-hidden="true" />
            </Button>
          </div>
          <CategorySection />
        </div>
        {home.data.featuredProducts &&
        home.data.featuredProducts.category.products.items.length >= 4 ?
          <div className={styles.popularProducts}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{home.data.featuredProducts.category.name}</h2>
              <ButtonLink
                className={styles.cta}
                variant="ghost"
                to={home.data.featuredProducts.category.url}
              >
                {t("HOMEVIEW.MORE_POPULAR_PRODUCTS")}
                <Arrow aria-hidden="true" />
              </ButtonLink>
            </div>
            <div className={styles.products}>
              {browser && home.data.featuredProducts.category.products.items
                .slice(0, getPopularProductAmount())
                .map((p, i) => (
                  <div key={p.name} className={styles.product}>
                    <ProductCard
                      product={p}
                      position={i}
                      list={home.data.featuredProducts.category.name}
                    />
                  </div>
                ))}
            </div>
          </div> : null}

        {offeringsContent && (
          <div className={styles.offeringsSection}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t("HOMEVIEW.OFFERINGS")}</h2>
            </div>
            <Offerings items={offeringsContent} />
          </div>
        )}

        {home.data.block2FullWidth && (
          <PromotionWrapper variant="full">
            <PromotionBlock {...home.data.block2FullWidth.block} variant="full" />
          </PromotionWrapper>
        )}

        {(home.data.block1Left && home.data.block1Right) && (
          <PromotionWrapper variant="double">
            <PromotionBlock {...home.data.block1Left.block} variant="double" />
            <PromotionBlock {...home.data.block1Right.block} variant="double" />
          </PromotionWrapper>
        )}
      </Wrapper>
    </div>
  );
};

export default Home;
